<script setup lang="ts">
	import type { ValidateErrorEntity } from 'ant-design-vue/lib/form/interface'
	import type { BusinessSignUpContext } from '~/types'
	import { supportLines } from '~/assets/json/customer-support'
	import useAccountApi from '~/services/apis/account'

	const { verifyingEmail, productName, productCategory } = defineProps<{
		verifyingEmail?: boolean
		productName?: string
		productCategory?: string
	}>()

	const formData = reactive<BusinessSignUpContext & { cac_verified: boolean; business_type: string | null }>({
		name: '',
		email: '',
		first_name: '',
		last_name: '',
		password: '',
		phone: '',
		cac_number: '',
		referral_channel: null,
		no_of_staff: null,
		user_type: 'business',
		cac_verified: false,
		business_type: null
	})
	const { params } = useRoute()
	const individualLinkOnboarding = `${import.meta.env.VITE_INDIVIDUAL_URL}/register/${params.product_id}?page=form`

	const { referralChannels } = useStore()
	const staffRange: SelectOptionContext = [
		{ label: 'Less than 2', value: '1' },
		{ label: '2-10', value: '2-10' },
		{ label: '11-20', value: '11-20' },
		{ label: '21-50', value: '21-50' },
		{ label: '51-100', value: '51-100' },
		{ label: '101-200', value: '101-200' },
		{ label: '201 and above', value: '201 and above' }
	]
	const cacTemplate = useTemplateRef('cacTemplate')

	const emit = defineEmits(['done'])

	const isFormReady = computed(() => {
		const { password, cac_number, no_of_staff, business_type, cac_verified, referral_channel, ...rest } = formData
		if (!no_of_staff || Number(no_of_staff) === 1) {
			return false
		}
		const isFieldsEmpty = Object.values(rest).some(item => !item)
		return !isFieldsEmpty
	})
	const skipCacField = shallowRef(false)
	const skippedKyc = shallowRef(false)
	const isSuccess = shallowRef(false)
	const noCac = computed(() => formData.business_type === 'none')

	const proceed = async () => {
		if (isFormReady.value) {
			if (formData.cac_number && !noCac.value) {
				if (cacTemplate.value) {
					const q = await cacTemplate.value?.handleValidation()
					if (q?.cac_verified) {
						const { name, ...rest } = q
						isSuccess.value = true
						emit('done', { ...formData, ...rest })
					}
				}
			} else {
				if (noCac.value) {
					formData.cac_number = ''
					skipCacField.value = true
				}
			}
		}
	}
	const businessInitial = shallowRef('')
	const checkBusinessType = (value: SelectOptionContext[number]) => {
		if (value.value === 'none') {
			skipCacField.value = true
			return
		}

		if (value?.initial) {
			businessInitial.value = value.initial as string
		}
	}
	const listenForError = (e: ValidateErrorEntity) => {
		if (e.errorFields.length) {
			const field = e.errorFields[0]
			useNotification().alert({
				message: field.errors[0],
				status: 400
			})
		}
	}
	const isValidating = shallowRef(false)
	const { openInterCom } = useIntercom()

	type FormKeys = keyof typeof formData
	type FieldReturnType = Partial<Record<FormKeys, FormRecordsContext>>
	const formFields = computed<(arg: 'business-info' | 'personal-info', returnOnly?: FormKeys[]) => FieldReturnType>(
		() => (arg, returnOnly) => {
			switch (arg) {
				case 'business-info':
					const businessInfo: FieldReturnType = {
						name: {
							type: 'text',
							label: 'Business Name',
							name: 'name',
							form_name: 'name',
							value: '',
							rules: { required: true, min: 3 },
							placeholder: skipCacField.value
								? 'Enter your business name'
								: 'Enter name as it appears on your company registration documents',
							testid: 'business_name',
							disabled: !!formData.cac_verified
						},
						business_type: {
							type: 'select',
							form_name: 'business_type',
							name: 'business_type',
							value: '',
							rules: { required: true },
							placeholder: 'Select business type',
							testid: 'business_type',
							disabled: !!formData.cac_verified
						},
						cac_number: {
							type: 'text',
							label: 'C.A.C Number',
							name: 'cac_number',
							form_name: 'cac_number',
							value: '',
							rules: { required: true },
							placeholder: 'Enter C.A.C number',
							testid: 'cac_number',
							disabled: !!formData.cac_verified
						},
						email: {
							type: 'email',
							label: `<span class="space-x-1 leading-snug">
                                        Email Address
                                        <span class="text-warning-500 text-xs">
                                            (OTP will be sent here, ensure you have access to this email)
                                        </span>
                                    </span>`,
							name: 'email',
							rules: { required: true, type: 'email' },
							form_name: 'email',
							value: '',
							placeholder: 'Enter email',
							testid: 'email'
						},
						phone: {
							type: 'text',
							label: 'Phone Number',
							name: 'phone',
							form_name: 'phone',
							value: '',
							placeholder: 'Enter business phone number',
							testid: 'phone'
						},
						no_of_staff: {
							type: 'select',
							label: 'Number of Staff',
							name: 'no_of_staff',
							form_name: 'no_of_staff',
							value: '',
							rules: { required: true },
							placeholder: 'Select number of staff',
							testid: 'no_of_staff'
						}
					}
					if (returnOnly) {
						return Object.fromEntries(
							Object.entries(businessInfo).filter(([key]) => returnOnly.includes(key as FormKeys))
						)
					}
					return businessInfo
				case 'personal-info':
					const personalInfo: FieldReturnType = {
						first_name: {
							type: 'text',
							label: 'First Name',
							name: 'first_name',
							form_name: 'first_name',
							value: '',
							rules: { required: true },
							hidden: false,
							placeholder: 'Enter your first name'
						},
						last_name: {
							type: 'text',
							label: 'Last Name',
							name: 'last_name',
							form_name: 'last_name',
							value: '',
							rules: { required: true },
							hidden: false,
							placeholder: 'Enter your last name'
						},
						user_phone: {
							type: 'text',
							label: 'Phone Number',
							name: 'user_phone',
							form_name: 'user_phone',
							value: '',
							rules: { required: true },
							hidden: false,
							placeholder: 'Enter your personal phone number'
						},
						referral_channel: {
							type: 'select',
							label: 'How did you hear about us?',
							name: 'referral_channel',
							form_name: 'referral_channel',
							value: '',
							rules: { required: true },
							placeholder: 'Select how you heard about us',
							testid: 'referral_channel',
							hidden: false,
							showSearch: true,
							options: referralChannels
						}
					}
					if (returnOnly) {
						return Object.fromEntries(
							Object.entries(personalInfo).filter(([key]) => returnOnly.includes(key as FormKeys))
						)
					}
					return personalInfo
			}
		}
	)

	const formForNonCAC = reactive({
		name: '',
		phone: ''
	})
	const { saveUnregisteredBusiness } = useAccountApi()
	const savingUnregisteredBusiness = ref(false)
	const handleNonCACFormSubmit = async () => {
		const response = await resolve(
			async () =>
				saveUnregisteredBusiness({
					name: formForNonCAC.name,
					phone_number: formForNonCAC.phone,
					product: productName || '',
					product_category: productCategory || ''
				}),
			savingUnregisteredBusiness
		)
		if (response.data?.id) {
			skippedKyc.value = true
			formForNonCAC.name = ''
			formForNonCAC.phone = ''
		}
	}

	watch(
		() => verifyingEmail,
		() => {
			if (!verifyingEmail) {
				skipCacField.value = false
			}
		}
	)

	useReloadWarning()
</script>

<template>
	<section class="w-full">
		<a-form
			:model="formData"
			:validate-messages="antValidateMessages"
			:scroll-to-first-error="true"
			:colon="false"
			class="overflow-hidden transition-all ease-in-out"
			@finish="proceed"
			@finish-failed="listenForError"
		>
			<form-auth-account-form-steps
				card-class="!bg-transparent"
				class="rounded border-gray-200 sm:border sm:bg-white"
			>
				<template #aboutBusiness>
					<div class="pt-6" />

					<forms-fields
						v-model="formData"
						tag="ul"
						class="space-y-5 pt-2"
						item-cls="mt-3"
						:items="formFields('business-info')"
					>
						<template #default="item">
							<forms-business-type-field
								v-if="item.form_name === 'business_type'"
								v-model="formData.business_type"
								:required="true"
								@selected="checkBusinessType"
							/>

							<template v-if="item.form_name === 'cac_number'">
								<forms-cac-field
									v-if="formData.business_type !== 'none'"
									v-model="formData.cac_number"
									v-model:validating-number="isValidating"
									ref="cacTemplate"
									:disabled="
										!formData.name || formData.business_type === 'none' || !formData.business_type
									"
									:business-name="formData.name"
									:required="formData.business_type !== 'none'"
									:type="businessInitial"
								/>
								<shared-highlight-note
									v-else
									:ui="{
										wrapper: 'px-3 py-2 bg-orange-50 border-orange-500',
										text: 'text-sm text-gray-600 font-medium',
										icon: 'fill-orange-500',
										container: 'items-center'
									}"
								>
									CAC verification helps us confirm your business and unlock access to more insurance
									benefits.
								</shared-highlight-note>
							</template>

							<template v-if="item.form_name === 'no_of_staff'">
								<a-form-item name="no_of_staff" label="Number Of Staff" required :colon="false">
									<a-select
										v-model:value="formData.no_of_staff as string"
										:options="staffRange"
										placeholder="0"
										class="w-full"
										data-testid="no_of_staff"
										show-search
										allow-clear
									/>
								</a-form-item>
								<shared-highlight-note
									v-if="formData.no_of_staff == String(1)"
									:ui="{
										wrapper: 'px-3 py-2 bg-error-50 border-error-500',
										text: 'text-sm text-gray-600',
										icon: 'fill-error-500'
									}"
								>
									Oops! Your business needs at least
									<span class="font-semibold text-gray-800">two staff members</span> to qualify for
									coverage. Register as an
									<nuxt-link
										:to="individualLinkOnboarding"
										external
										target="_blank"
										class="font-semibold text-main underline"
										rel="noreferrer"
									>
										individual customer
									</nuxt-link>
									or contact our
									<span
										role="button"
										aria-label="support button"
										class="font-semibold text-main"
										@click="openInterCom"
									>
										support team
									</span>
									for help.
								</shared-highlight-note>
							</template>
						</template>
					</forms-fields>
				</template>

				<template #introduction>
					<forms-fields
						v-model="formData"
						tag="ul"
						class="space-y-5 pt-2"
						item-cls="mt-3"
						:items="formFields('personal-info')"
					/>
				</template>
			</form-auth-account-form-steps>

			<ui-button
				type="submit"
				data-testid="proceed"
				class="mt-10 w-full justify-center bg-primary-2 text-white"
				:loading="verifyingEmail"
				:disabled="verifyingEmail"
			>
				{{ isValidating ? 'Verifying Business...' : verifyingEmail ? 'Hold on...' : 'Proceed' }}
			</ui-button>
		</a-form>

		<dialogs
			v-model:show="skipCacField"
			btn-position="center"
			:title="!skippedKyc ? 'Let\'s help you get started.' : ''"
			:ui="{
				modal: 'pb-5 max-w-[564px] w-full flex flex-col justify-center items-center px-0',
				modalBody: 'w-full relative',
				title: `py-[15px] text-center w-full ${!skippedKyc ? 'border-b-0' : ''}`,
				header: 'w-full'
			}"
			@change="skippedKyc = false"
		>
			<a-form
				v-show="!skippedKyc"
				:validate-messages="antValidateMessages"
				:model="formForNonCAC"
				@finish="handleNonCACFormSubmit"
			>
				<div class="w-full">
					<p class="pb-[24px] text-center text-base font-medium text-gray-600">
						Share your business details below and our team will contact you.
					</p>

					<forms-fields
						v-model="formForNonCAC"
						tag="ul"
						class="w-full space-y-5"
						item-cls="mb-3 w-full"
						:items="formFields('business-info', ['name', 'phone'])"
					/>
				</div>

				<div class="flex items-center justify-between gap-x-[42px] pt-[20px]">
					<ui-button
						text="Submit"
						variants="one"
						type="submit"
						class="w-full py-[10px] sm:px-[10px]"
						:loading="verifyingEmail || savingUnregisteredBusiness"
					/>
				</div>
				<p class="pt-[20px] text-center text-base font-medium text-gray-600">
					In a hurry?
					<span class="font-semibold text-main" @click="openInterCom">Reach out to our support team</span>
				</p>
			</a-form>
			<transition name="zoom" appear-active-class="animate__animated animate__zoomIn animate__faster">
				<div
					v-if="skippedKyc"
					class="flex h-full w-full flex-col items-center justify-center space-y-2 px-2 py-3.5 text-center sm:pb-[60px] sm:pt-[30px]"
				>
					<tools-icon dir="fill" icon="success-check" />

					<ui-title-header
						title="Business details saved"
						subtitle="Our support team will reach out to you as soon as possible."
						title-cls="text-[18px] sm:text-[28px] leading-[36px] font-semibold text-blue-950"
						subtitle-cls="text-[14px] sm:text-[16px] leading-[24px] font-medium text-gray-600"
						class="space-y-[20px] pt-[40px]"
					/>
				</div>
			</transition>
		</dialogs>
	</section>
</template>
